import { Button, CircularProgress, Grid, Skeleton, Box, Avatar, Checkbox, MenuItem, Select, FormControl, ListItemText } from "@mui/material";
import { DataGridPro, gridDateComparator } from "@mui/x-data-grid-pro";
import React, { Fragment, useState, useEffect, useContext } from "react";
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { useSelector } from "react-redux";
import { selectAllBuyRequests, selectAllEvents, selectAllSwarms, selectDeliveryMethods, selectRunEvents } from "../../store/slices/swarms";
import { useEditBuyRequestMutation, useLazyGetBuyRequestQuery } from '../../services/swarmsApi';
import { useNotification } from '../../context/NotificationContext';
import AppContext from "../../context/AppProvider";
import { ReasonMenu } from "../../components/reasonMenu";
import { ApplicationTable } from "../../constants/enums";
import { useGetUserStyleOrDefaultQuery, useUpdateColumnsForUserMutation } from '../../services/tableStyleApi';
import { BUY_REQUEST_GRID_COLUMN } from './PendingBuyRequestGridColumn';
import useDidMountEffect from '../../customHooks/useDidMountEffect';
import { BuyRequestStatus } from "../../constants/enums";
import { sendSignalRSafe } from "../../utils/signalRSender";
import VenueMapDialog from "./VenueMapDialog/VenueMapDialog";
import { selectAllBuyManagers } from "../../store/slices/buyManager/buyManagers";
import { Tooltip } from '@mui/material';
import { selectCurrentUser } from "../../features/auth/authSlice";
import { useDeliveryMethodsQuery } from "../../services/jobsLookUpTables";
import { useDisapprovalReasons } from '../../customHooks/useDisapprovalReasons';
import { useLazyGetEventsQuery } from "@/services/eventApi";


const ActionButtomsCell = ({ props, setShowError }) => {

  const { showNotification } = useNotification();
  
  const buyRequests = useSelector(selectAllBuyRequests);
  const swarms = useSelector(selectAllSwarms);
  const runEvents = useSelector(selectRunEvents);
  const [editBuyRequest] = useEditBuyRequestMutation();
  const [getBuyRequest] = useLazyGetBuyRequestQuery();
  const { disapprovalReasons } = useDisapprovalReasons();
  const [loading, setLoading] = useState('')

  //create a dictionary where each key is a swarmId and the value is bteId field
  let swarmsDict = {};
  swarms?.forEach(s => swarmsDict[s.id] = s.bteId);

  //A: create a dictionary where each key is a userId and the value is an object where each key is an bteId that the user has a buyRequest with swarmResolutionTypeId == acceptedId
  let acceptedBuyRequestsDict = {};
  buyRequests?.filter(br => br.swarmResolutionTypeId == BuyRequestStatus.Accepted).forEach(br => {
    if (!acceptedBuyRequestsDict[br.userId]) acceptedBuyRequestsDict[br.userId] = {};
    acceptedBuyRequestsDict[br.userId][swarmsDict[br.swarmId]] = true;
  });
  // TODO: improve readability end effiecienty of dict (use maps, enclose logic in functions, explain logic within comments and add examples)
  // search for run event ids and add them to the dict
  Object.keys(acceptedBuyRequestsDict)?.forEach(userId => {
    let userDict = acceptedBuyRequestsDict[userId];
    Object.keys(userDict)?.forEach(bteId => {
      runEvents?.forEach(event => {
        if (event.bteId == bteId) {
          event.runEventIds?.forEach(runEventId => {
            userDict[runEventId] = true;
          });
        }
      });
    });
  });

  const haveAlreadyApproved = () => {
    return acceptedBuyRequestsDict[props.row.userId]?.[swarmsDict[props.row.swarmId]] 
  }

  // TODO: unify the buy and pass buttons click functions into one, in order to avoid code duplication
  async function onBuyClick() {
    try {
      setLoading("BUY");
      await editBuyRequest(
        {
          id: props.row.id,
          swarmResolutionTypeId: BuyRequestStatus.Accepted
        }
      ).unwrap();
      setLoading(false);
    } catch (error) {
      setLoading(false);
      if (error.status == 401) return; //error managed on baseQueryWithReauth
      if (error.status == 400) { //A: Backend validation error
        showNotification(error.data.message, 'error');
        await getBuyRequest({ query: { swarmResolutionTypeId: BuyRequestStatus.Pending } }).unwrap().catch(e => {
          if (e.status == 401) return; //error managed on baseQueryWithReauth
          throw e;
        });
        return;
      }
      throw error;
    }
  }

  async function onPassClick() {
    try {
      setLoading("PASS");
      await editBuyRequest(
        {
          id: props.row.id,
          swarmResolutionTypeId: BuyRequestStatus.Rejected,
        }
      ).unwrap();
      setLoading('');
    } catch (error) {
      setLoading('');
      if (error.status == 401) return; 
      if (error.status == 400) { 
        showNotification(error.data.message, 'error');
        await getBuyRequest({ query: { swarmResolutionTypeId: BuyRequestStatus.Pending } }).unwrap().catch(e => {
          if (e.status == 401) return; 
          throw e;
        });
        return;
      }
      throw error;
    }
  }

  async function onReasonClick(reason) {
    try {
      setLoading("REASON");
      await editBuyRequest({
        id: props.row.id,
        swarmResolutionTypeId: BuyRequestStatus.Rejected,
        reasonId: reason
      }).unwrap();
      setLoading('');
    }
    catch (error) {
      setLoading('');
      if (error.status == 401) return; 
      if (error.status == 400) { 
        showNotification(error.data.message, 'error');
        await getBuyRequest({ query: { swarmResolutionTypeId: BuyRequestStatus.Pending } }).unwrap().catch(e => {
          if (e.status == 401) return; 
          throw e;
        });
        return;
      }
      throw error;
    }
  }

  const processedStyle = {
    width: 1,
    height: 1,
    backgroundColor: '#BDBDBD',
    color: "white",
    borderRadius: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  if (props.row.swarmResolutionTypeId == BuyRequestStatus.Pending) {
    return (
      <div
        style={{ display: 'flex', flexWrap: 'nowrap' }}
      >

        <Button
          onClick={onBuyClick}
          variant="contained"
          color="success"
          sx={{ borderRadius: 1, padding: '4px 6px', minWidth: 50 }}
          //the button should we disabled if the user has a buyRequest with the same bteId and swarmResolutionTypeId == acceptedId
          disabled={ haveAlreadyApproved() || Boolean(loading)}
        >
          {loading === "BUY" ? <CircularProgress size="1.5em" /> : "BUY"}
        </Button>

        <Button
          onClick={onPassClick}
          disabled={Boolean(loading)}
          variant="contained"
          color="error"
          sx={{ borderRadius: 1, margin: '0 0.25em', padding: '4px 6px', minWidth: 50 }}
        >
          {loading === "PASS" ? <CircularProgress size="1.5em" /> : "PASS"}
        </Button>

        <ReasonMenu reasons={disapprovalReasons} onSelect={onReasonClick} loading={loading}>
          <Button
            variant="contained"
            color="error"
            sx={{ borderRadius: 1, padding: '4px 6px', minWidth: 50 }}
            disabled={Boolean(loading)}
          >
            {
              loading === "REASON"
                ?
                <CircularProgress size="1.5em" />
                :
                "REASON"
            }
          </Button>
        </ReasonMenu>

      </div>
    )
  }

  else {
    return (<Box sx={processedStyle}>
      {props.row.swarmResolutionTypeId === BuyRequestStatus.Accepted
        ? "APPROVED"
        : props.row.swarmResolutionTypeId === BuyRequestStatus.Rejected
        ? "REJECTED"
        : props.row.swarmResolutionTypeId === BuyRequestStatus.Completed
        ? "COMPLETED"
        : props.row.swarmResolutionTypeId === BuyRequestStatus.Lost
        ? "LOST"
        : props.row.swarmResolutionTypeId === BuyRequestStatus.OverTheLimit
        ? "OVER THE LIMIT"
        :"CANCELLED"}
    </Box>)
  }

}

const BuyManagerSelector = ({buyManagers, selectedBuyManagers, setSelectedBuyManagers,userId}) => {
  const handleChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedBuyManagers(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  return(
    <div>
      <FormControl sx={{ m: 1, width: 300 }}>
        <Select
          labelId="demo-multiple-checkbox-label"
          id="demo-multiple-checkbox"
          multiple
          value={selectedBuyManagers}
          onChange={handleChange}
          renderValue={(selectedIds) => {
            const selectedBMs = buyManagers.filter(BM => selectedIds.includes(BM.buyManagerID));
            return selectedBMs.map(BM => `${BM.firstName} ${BM.lastName}`).join(', ');
          }}
          sx={{width:"300px", height:"2em", backgroundColor:"white"}}
        >
          {buyManagers?.map((BM) => (
            <MenuItem key={BM.buyManagerID} value={BM.buyManagerID}>
              <Checkbox checked={selectedBuyManagers.includes(BM.buyManagerID)}/>
              <ListItemText 
                primary={
                  BM.encoreHiveId == userId
                  ? "Show Mine"
                  : BM?.firstName + " " + BM?.lastName
                }
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  )
}


const PendingBuyRequests = ({ showError, setShowError }) => {
  const { signalRUserConnection } = useContext(AppContext);
  const user = useSelector(selectCurrentUser);
  
  const [activeUsersStat, setActiveUsersStat] = useState(null);

  const buyRequests = useSelector(selectAllBuyRequests);
  const {data:deliveryMethods} = useDeliveryMethodsQuery();
  const [getBuyRequests] = useLazyGetBuyRequestQuery();
  
  const events = useSelector(selectAllEvents);
  const [pendingBuyRequestGrid, setPendingBuyRequestGrid] = useState([]);
  const [rows, setRows] = useState([])

  const [columns, setColums] = useState([])
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({})

  const { data: styles } = useGetUserStyleOrDefaultQuery(ApplicationTable.PendingBuyRequest);
  const [updateColumnsForUser] = useUpdateColumnsForUserMutation();
  const [gridStyle, setGridStyle] = useState(null);
  const [isFirstGridLoad, setIsFirstGridLoad] = useState(true);
  const [isFirstGridLoadBuyRequest, setIsFirstGridLoadBuyRequest] = useState(true);

  const [isVenueMapOpened, setIsVenueMapOpened] = useState(false);
  const [venueMapData, setVenueMapData] = useState(null);
  const buyManagers = useSelector(selectAllBuyManagers);
  const [selectedBuyManagers, setSelectedBuyManagers] = useState([])

  const [getEvents, {}] = useLazyGetEventsQuery();
  
  useEffect(()=>{
    if(buyManagers && user){
      const userBuyManager = buyManagers.find(bm => bm.encoreHiveId === user.id);
      if (userBuyManager) {
        setSelectedBuyManagers([userBuyManager.buyManagerID]);
      }
    }
  },[buyManagers,user]);

  const gridColumns = {
    [BUY_REQUEST_GRID_COLUMN.ID]: { 
      headerName: "ID", 
      field: 'id' 
    },
    [BUY_REQUEST_GRID_COLUMN.EVENT]: { 
      headerName: "EVENT", 
      field: "event", 
      flex: 1.5, 
      renderCell: v => !v.value ? <Skeleton variant="text" width="70%" /> : v.value 
    },
    [BUY_REQUEST_GRID_COLUMN.VENUE]: { 
      headerName: "VENUE", 
      field: "venue", 
      flex: 1.5, 
      renderCell: renderVenueColumn
    },
    [BUY_REQUEST_GRID_COLUMN.EVENT_DATE]: { 
      headerName: "EVENT DATE", 
      field: "date", 
      flex: 1, 
      sortComparator: gridDateComparator, 
      renderCell: renderEventDateCell 
    },
    [BUY_REQUEST_GRID_COLUMN.QUANTITY]: { 
      headerName: "QUANTITY", 
      field: "quantity", 
      flex: 0.5, 
      headerAlign: 'center', 
      align: 'center' 
    },
    [BUY_REQUEST_GRID_COLUMN.SECTION]: { 
      headerName: "SECTION", 
      field: "section", 
      flex: 0.5, 
      headerAlign: 'center', 
      align: 'center' 
    },
    [BUY_REQUEST_GRID_COLUMN.ROW]: { 
      headerName: "ROW", 
      field: "row", 
      flex: 0.5, 
      headerAlign: 'center', 
      align: 'center' 
    },
    [BUY_REQUEST_GRID_COLUMN.SEATS]: { 
      headerName: "SEATS", 
      field: "seats", 
      flex: 0.5 
    },
    [BUY_REQUEST_GRID_COLUMN.DELIVERY_METHOD]: { 
      headerName: "DELIVERY METHOD", 
      field: "delivery", 
      flex: 1, 
      valueGetter: v => getDeliveryMethodName(v.row.deliveryMethodID) 
    },
    [BUY_REQUEST_GRID_COLUMN.NTL]: { 
      headerName: "", 
      field: "ntl", 
      width: 50, 
      renderCell: params => params.row.isNonTransferrableLanguage && <div className="non-transferrable-language">NTL</div> 
    }, 
    [BUY_REQUEST_GRID_COLUMN.COST_PER_TICKETS]: { 
      headerName: "COST PER TICKET", 
      field: "reward", 
      flex: 0.50, 
      headerAlign: 'center', 
      align: 'center', 
      valueGetter: v => v?.row?.totalCost / v.row.quantity, 
      renderCell: v => '$ ' + v.value.toFixed(2) 
    },
    [BUY_REQUEST_GRID_COLUMN.WAITING]: { 
      headerName: "WAITING", 
      field: "waiting", 
      flex: 0.50, 
      headerAlign: 'center', 
      align: 'center', 
      sortComparator: (a, b) => gridDateComparator(b, a), 
      valueGetter: v => v.row.createdOn, 
      renderCell: (v) => <WaitingCell ts={(new Date(v.value.toLowerCase().endsWith('z') ? v.value : v.value + 'Z')).valueOf()}/> 
    },
    [BUY_REQUEST_GRID_COLUMN.BUY_MANAGER]: { 
      headerName: "", 
      field: "buyManager", 
      flex: 0.50, 
      headerAlign: 'center', 
      align: 'center', 
      renderCell: (params) => {
        if(!params.value) return
        const avatar = {color: params.value?.color ||'#FFB74D', initials: Array.from(params.value?.firstName[0]) + Array.from(params.value?.lastName)[0]}
        return (
          <Tooltip title={`${params.value?.firstName} ${params.value?.lastName}`}>
            <Avatar sx={{ backgroundColor: avatar?.color || '#FFB74D', height:"30px", width:"30px", fontSize:"12px", fontWeight:"700" }}>
              {avatar.initials}
            </Avatar>
          </Tooltip>
        );
      },
    },
    [BUY_REQUEST_GRID_COLUMN.MANAGE]: { 
      headerName: "", 
      field: "buy", 
      resizable: false, 
      filterable: false, 
      hideSortIcons: true, 
      sortable: false, 
      width: 195, 
      renderCell: (params) => <ActionButtomsCell props={params} setShowError={setShowError} /> 
    },
  };

  useEffect(() => {
    if (!signalRUserConnection)
      return;

    if (!activeUsersStat) {
      sendSignalRSafe(signalRUserConnection, async (connection) => {
        await connection.send('GetUserCountStaistic');
      }) 
    }

    signalRUserConnection.on('UpdateUserCount', (userCount) => {
      setActiveUsersStat(userCount)
    });

    return () => {
      signalRUserConnection.off('UpdateUserCount')
    }
  }, [signalRUserConnection])

  const mapBuyRequestToRow = (br, events, buyManagers) => {
    console.log("mapBuyRequestToRow bteId", br.bteId)
    console.log("mapBuyRequestToRow events", events)
    const brEvent = events?.find((e) => e?.bteId === br?.bteId);
    const brBuyManager = buyManagers?.find(BM => BM?.buyManagerID == br?.buyManagerId)

    return {
      ...br,
      venue: !brEvent?.venue?.name ? null : brEvent?.venue?.name + ', ' + brEvent?.venue?.city,
      event: brEvent?.name,
      exchangeId: brEvent?.exchangeId,
      date: brEvent?.date,
      reward: br?.reward,
      buyManager: brBuyManager
    };
  };

  const getSavedGrid = () => {
    return sessionStorage.getItem("PENDING_BUY_REQUEST_GRID") ? JSON.parse(sessionStorage.getItem("PENDING_BUY_REQUEST_GRID")) : []
  }

  useEffect(async () => {
    let resTypes = [BuyRequestStatus.Pending, BuyRequestStatus.Accepted];
    try {
      await getBuyRequests({ SwarmResolutionTypeId: resTypes }).unwrap()
    } catch (error) {
      if (error.status == 401) return;
      throw error;
    }
    finally {
      setIsFirstGridLoadBuyRequest(false)
    }
  }, [])

  useEffect(() => {
    
    if(pendingBuyRequestGrid && buyManagers){
      let rows = pendingBuyRequestGrid.map((br) => mapBuyRequestToRow(br, events, buyManagers));
      if(selectedBuyManagers?.length>0){
        rows = rows.filter((row) => selectedBuyManagers.includes(row.buyManager?.buyManagerID));
      }
      rows.sort((a, b) => {
        let dateA = new Date(a.createdOn);
        let dateB = new Date(b.createdOn);
        return dateA - dateB;
      })
      setRows([...rows])
    }
  }, [pendingBuyRequestGrid, events, buyManagers, selectedBuyManagers])

  useDidMountEffect(() => {
    const brIds = pendingBuyRequestGrid.map(br => br.id);
    sessionStorage.setItem("PENDING_BUY_REQUEST_GRID", JSON.stringify(brIds))
  }, [pendingBuyRequestGrid])

  useEffect(async () => {
    if (!buyRequests || isFirstGridLoadBuyRequest) 
      return;

    const buyRequestIds = getSavedGrid().filter(brId => !buyRequests.some(r => r.id === brId));

    if (buyRequestIds.length !== 0)
    {
      try {
        await getBuyRequests({BuyRequestId: buyRequestIds, extraOptions: true})
      } catch (error) {
        if (error.status == 401) return;
        throw error;
      }
    }

    const updatedGrid = getSavedGrid().map(brId => buyRequests.find(x => x.id === brId)).filter(x => !!x);

    const newPendingRows = buyRequests.filter(
      (br) => br.swarmResolutionTypeId === BuyRequestStatus.Pending && !getSavedGrid().some((rId) => rId === br.id)
    );

    const totalBuyRequest= [...updatedGrid, ...newPendingRows];
    setPendingBuyRequestGrid(totalBuyRequest);

    const eventsIds = totalBuyRequest.map(br => br.bteId);
    const eventsIdsUnique = [...new Set(eventsIds)];
    //return if there are no events to fetch
    if(!eventsIdsUnique.length) return;
    const eventsIdsToFetch = eventsIdsUnique.filter(eId => !(events || []).some(e => e.bteId === eId));

    const query = eventsIdsToFetch.map(eId => `bteId=${eId}`).join('&');
    
    //A. when this method is called, automatically we update the redux store
    getEvents(`?${query}`);
  }, [buyRequests, isFirstGridLoadBuyRequest])

  useEffect(() => {
    if (!styles)
      return;

    setGridStyle([...styles]);
  }, [styles])

  useEffect(async () => {
    if (!styles || !gridStyle)
      return;

    let displayColumns = [];
    let hideColumns = {};

    const sortedColumns = [...gridStyle].sort((a, b) => a.index - b.index);
    sortedColumns.forEach(column => {
      let displayColumn = gridColumns[column.name];
      
      if(!displayColumn){
        console.error("Column Not Found, column: ", column, " gridColumns: ", gridColumns);
      } else {        
        if (column.width) {
          displayColumn.flex = 0;
          displayColumn.width = column.width;
        }

        hideColumns[displayColumn.field] = column.mustBeVisible;
        displayColumns.push(displayColumn);
      }
    });

    setColumnVisibilityModel(hideColumns);
    setColums(displayColumns);

    if (!isFirstGridLoad) {
      await updateColumnsForUser({ table: ApplicationTable.PendingBuyRequest, columns: gridStyle }).unwrap();
    }
    setIsFirstGridLoad(false);
  }, [gridStyle])

  const onShowVenueMapClick = (exchangeId, seatsData) => {
    setVenueMapData({
      eventId: exchangeId,
      seatsData: [seatsData]
    })
    setIsVenueMapOpened(true)
  }

  const onCloseVenueMapClick = () => {
    setIsVenueMapOpened(false)
  }

  const onRefreshClick = () => {
    const resTypes = [BuyRequestStatus.Pending, BuyRequestStatus.Accepted];

    setPendingBuyRequestGrid([])
    try {
      getBuyRequests({ SwarmResolutionTypeId: resTypes }).unwrap()
    } catch (error) {
      if (error.status == 401) return;
      throw error;
    }
  }

  const WaitingCell = ({ ts }) => {
    const [now, setNow] = useState(Date.now());

    useEffect(() => {
      const interval = setInterval(() => { setNow(Date.now()); }, 1000);
      return () => { clearInterval(interval) };
    }, []);

    if (ts === NaN || ts == null || isNaN(ts)) return 'Error';
    let hours = parseInt((Math.max(0, (now - ts)) / (60 * 60 * 1000)) % 60);
    let minutes = parseInt((Math.max(0, (now - ts)) / (60 * 1000)) % 60);
    let seconds = parseInt(Math.max(0, ((now - ts)) / (1000)) % 60);

    hours = hours.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })
    minutes = minutes.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })
    seconds = seconds.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false })
    return (<>{`${minutes}:${seconds}`}</>)
  }

  function getDeliveryMethodName(deliveryMethodId) {
    const dm = deliveryMethods?.find(dm => dm.id == deliveryMethodId);
    return dm?.name || 'N/A';
  }

  const onColumnVisibilityModelChange = (newModel) => {
    if (Object.keys(newModel).length === 0)
      Object.entries(gridColumns).forEach(([key, value]) => newModel[value.field] = true)

    let newStyle = gridStyle.map(style => {
      let gridRow = gridColumns[style.name]

      const [key, value] = Object.entries(newModel).find(([key, column]) => key === gridRow.field) || [];

      return {
        ...style,
        mustBeVisible: key ? value : style.mustBeVisible
      }
      });

    setGridStyle(newStyle);
  }

  const onColumnWidthChanged = ({ colDef, element, width }) => {
    const [key] = Object.entries(gridColumns).find(([key, column]) => column.field === colDef.field)

    let newStyle = gridStyle.map(style => {
      const isSelectedStyle = style.name === key

      return {
        ...style,
        width: isSelectedStyle ? Math.round(width) : style.width
      }
    });

    setGridStyle(newStyle);
  }

  const onColumnOrderChange = ({ colDef, element, field, oldIndex, targetIndex }) => {
    const [key] = Object.entries(gridColumns).find(([key, column]) => column.field === field)

    ++targetIndex; ++oldIndex;
    let newStyle = gridStyle.map(style => {
      const isSelectedStyle = style.name === key;
      const isPrevColumn = style.index === targetIndex;

      return {
        ...style,
        index: isSelectedStyle ? targetIndex : isPrevColumn ? oldIndex : style.index
      }
    });

    setGridStyle(newStyle);
  }

  function renderEventDateCell(params) {
    if (!params.value) {
      return <Skeleton variant="text" width="70%" />;
    }

    const eventDate = new Date(params.value);
    const options = {
      weekday: "short",
      month: "numeric",
      day: "numeric",
      year: "2-digit",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };

    const formattedDate = eventDate.toLocaleString("en-US", options)
      .replace(/(?<=\b)(Mon|Tue|Wed|Thu|Fri|Sat|Sun)(?=\b)/g, match => {
        switch (match) {
          case "Mon":
            return "Mo";
          case "Tue":
            return "Tu";
          case "Wed":
            return "We";
          case "Thu":
            return "Th";
          case "Fri":
            return "Fr";
          case "Sat":
            return "Sa";
          case "Sun":
            return "Su";
          default:
            return match;
        }
      })
      .replace(",", "") // Remove the comma after the weekday
      .replace(/,\s/g, " ") // Remove the comma after the date
      .replace(/\//g, "-") // Replace '/' with '-'
      .replace(/\b0/g, "") // Remove leading zeros from the date
      .replace(":0 ", " "); // Remove the minutes part when it's an oclock time

    return formattedDate;
  }

  function renderVenueColumn(params) {
    if (!params.value)
      return <Skeleton variant="text" width="70%" />

    return (
      <Box onClick={() => 
        params.row.exchangeId ? 
          onShowVenueMapClick(
            params.row.exchangeId, 
            {
              tgID: params.row.id,
              tgUserSec: params.row.section,
              tgUserRow: params.row.row,
              tgUserSeats: params.row.seats,
              tgQty: params.row.quantity,
              tgPrice: params.row.reward,
              tgNotes: ''
            }) 
          : null
        } 
        className={params.row.exchangeId ? "clickable-block" : ""}
      >
        {params.value}
      </Box>
    )
  }

  return (
    <Fragment>
      <Grid container sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin:'1em 0 1em 0'}} >
      
        <Box container sx={{ display: 'flex' }} >
          <h3 style={{ margin: "0 0 0.5rem 0", fontWeight: '600' }}>Pending Buy Requests</h3>

          <div style={{ display: 'flex', marginLeft:'2em', marginTop: '1em'}}>
            <div style={{display: 'flex', marginLeft:'2em', fontSize:'12px'}}>

              <div 
                className="non-transferrable-language"
                style={{margin:'-5px 0.5em 0 0'}}
              >
                NTL
              </div>
              Non-Transferrable Language
            </div>

            <div style={{display: 'flex', marginLeft:'2em', fontSize:'12px'}}>
              <div 
                className="pending-buy-request-limited-row"
                style={{width:'32px', height:'14px', borderRadius:'4px', marginRight:'0.5em'}}
              >
              </div>
              Obstructed/Limited View
            </div>
            
          </div>
        </Box>

        <Grid sx={{display: 'flex'}}>
          <Grid sx={{ display: 'flex', alignItems: 'center', marginRight: '15px' }} >
            <BuyManagerSelector
              buyManagers={buyManagers}
              selectedBuyManagers={selectedBuyManagers}
              setSelectedBuyManagers={setSelectedBuyManagers}
              userId={user.id}
            />
            <GroupOutlinedIcon color="primary" />
            <p style={{ margin: "0 0 0 0.5em" }}>Active Users</p>
            <p style={{ margin: "0 0 0 0.5em", color: 'var(--orange)', fontWeight: '700' }}>
              <span style={{ fontSize: '1rem' }}>{activeUsersStat?.swarmBuyRequest?.activeUsersWithSwarms ?? 0}</span>
            </p>
          </Grid>
          <Button
            variant="outlined"
            sx={{ width: "80px", padding: 0.25 }}
            onClick={onRefreshClick}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      <VenueMapDialog open={isVenueMapOpened} handleClose={onCloseVenueMapClick} data={venueMapData}/>

      <DataGridPro
        headerHeight={40}
        rowHeight={36}
        hideFooter
        sx={{
          height: "46vh",
          borderRadius: "1em",
          backgroundColor: "white",
          padding: "0 0.5em 0.5em 0.5em",
          '&:disabled': { backgroundColor: "red" },
          margin: '0 0 1em 0'
        }}
        getRowClassName={(params) => {
          return params.row.swarmResolutionTypeId !== BuyRequestStatus.Pending
            ? 'data-grid-not-active-row'
            : params.row.isLimitedView
              ? 'pending-buy-request-limited-row'
            : '';
        }}
        isRowSelectable={() => false}
        columns={columns}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => onColumnVisibilityModelChange(newModel)}
        onColumnWidthChange={onColumnWidthChanged}
        onColumnOrderChange={onColumnOrderChange}
        rows={rows}
        hideFooterSelectedRowCount
        checkboxSelection={false}
        disableColumnPinning={true}
      />
    </Fragment>
  );
}

export default PendingBuyRequests;
